<template>
  <dialog-modal
    :open="open"
    as="div"
    @close="closeModal"
    @keyup.enter.exact.prevent="okAction(true)"
  >
    <div class="fixed inset-0 z-100 bg-gray-500/75 transition-opacity" />
    <div class="fixed inset-0 z-100 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center">
        <dialog-panel
          class="relative w-full overflow-visible rounded-lg text-left shadow-lg-cool-grey transition-all"
          :class="{
            'max-w-sm': size === 'sm',
            'max-w-md': size === 'md',
            'max-w-lg': size === 'lg',
            'max-w-xl': size === 'xl',
            'max-w-2xl': size === '2xl',
            'max-w-3xl': size === '3xl',
            'max-w-4xl': size === '4xl',
            'max-w-5xl': size === '5xl',
          }"
        >
          <dialog-title
            as="h3"
            class="flex rounded-t-lg px-4 pt-6 pb-4 text-xl font-semibold lg:text-2xl"
            :class="titleBackground"
          >
            <div class="grow">
              <slot name="title"></slot>
            </div>
            <div
              v-if="!noClose"
              class="mt-px cursor-pointer px-1 hover:text-primary-700"
              @click="closeModal()"
            >
              <i class="fal fa-times" aria-hidden="true"></i>
            </div>
          </dialog-title>
          <div
            class="flow-root px-4 pb-6"
            :class="[
              contentBackground,
              { 'pt-6': contentBackground !== titleBackground },
              { 'rounded-b-lg': hideFooter },
            ]"
          >
            <slot></slot>
          </div>
          <button-group
            v-if="!hideFooter"
            class="rounded-b-lg px-4 py-4"
            :class="footerBackground"
            justify="justify-between"
          >
            <plain-button
              v-if="third && thirdPosition === 'left'"
              class="self-start"
              data-cy="modal-third"
              :mobile-icon="thirdIcon !== ''"
              :icon="thirdIcon"
              :variant="thirdVariant"
              :loading="thirdSaving"
              @click="thirdAction"
              >{{ $t(thirdTitle) }}</plain-button
            >
            <button-group class="flex-1">
              <plain-button
                v-if="!okOnly"
                data-cy="modal-cancel"
                :mobile-icon="cancelIcon !== ''"
                :icon="cancelIcon"
                :variant="cancelVariant"
                :loading="cancelSaving"
                @click="cancelAction"
                >{{ $t(cancelTitle) }}
              </plain-button>
              <plain-button
                v-if="third && thirdPosition === 'middle'"
                data-cy="modal-third"
                :loading="thirdSaving"
                :mobile-icon="thirdIcon !== ''"
                :icon="thirdIcon"
                :variant="thirdVariant"
                @click="thirdAction"
                >{{ $t(thirdTitle) }}
              </plain-button>
              <plain-button
                data-cy="modal-ok"
                :loading="saving"
                :mobile-icon="okIcon !== ''"
                :icon="okIcon"
                :variant="okVariant"
                @click="okAction(false)"
                >{{ $t(okTitle) }}
              </plain-button>
            </button-group>
          </button-group>
        </dialog-panel>
      </div>
    </div>
  </dialog-modal>
</template>
<script setup lang="ts">
import {
  Dialog as DialogModal,
  DialogPanel,
  DialogTitle,
} from '@headlessui/vue';
import ButtonGroup from '@/components/shared/ButtonGroup.vue';
import { PropType, ref } from 'vue';
import { ButtonVariant } from '@/model/button';

const props = defineProps({
  saveFunction: {
    type: Function,
    default: () => ({}),
  },
  cancelFunction: {
    type: Function,
    default: () => ({}),
  },
  thirdFunction: {
    type: Function,
    default: () => ({}),
  },
  saving: {
    type: Boolean,
    default: false,
  },
  cancelSaving: {
    type: Boolean,
    default: false,
  },
  thirdSaving: {
    type: Boolean,
    default: false,
  },
  okOnly: {
    type: Boolean,
    default: false,
  },
  third: {
    type: Boolean,
    default: false,
  },
  okTitle: { type: String, default: 'button.save' },
  cancelTitle: { type: String, default: 'button.cancel' },
  thirdTitle: { type: String, default: 'button.reset' },
  okVariant: {
    type: String as PropType<ButtonVariant>,
    default: ButtonVariant.PRIMARY,
  },
  cancelVariant: {
    type: String as PropType<ButtonVariant>,
    default: ButtonVariant.TEXT,
  },
  thirdVariant: {
    type: String as PropType<ButtonVariant>,
    default: ButtonVariant.TEXT,
  },
  thirdPosition: {
    validator(value: string) {
      return value === 'left' || value === 'middle';
    },
    default: 'left',
  },
  okIcon: {
    type: String,
    default: '',
  },
  cancelIcon: {
    type: String,
    default: '',
  },
  thirdIcon: {
    type: String,
    default: '',
  },
  size: {
    type: String as PropType<
      'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'
    >,
    default: 'lg',
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
  noClose: {
    type: Boolean,
    default: false,
  },
  autoCloseOnOk: {
    type: Boolean,
    default: true,
  },
  autoCloseOnCancel: {
    type: Boolean,
    default: true,
  },
  forceCloseOnCancel: {
    type: Boolean,
    default: true,
  },
  autoCloseOnThird: {
    type: Boolean,
    default: true,
  },
  contentBackground: {
    type: String,
    default: 'bg-white',
  },
  titleBackground: {
    type: String,
    default: 'bg-white',
  },
  footerBackground: {
    type: String,
    default: 'bg-white',
  },
  okOnEnterKey: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['onOk', 'onCancel', 'onThird', 'onClose', 'onOpen']);
const open = ref(false);

function openModal() {
  emit('onOpen');
  open.value = true;
}
function okAction(enterKeyPressed: boolean) {
  if (!enterKeyPressed || (enterKeyPressed && props.okOnEnterKey)) {
    props.saveFunction();
    emit('onOk');
    if (props.autoCloseOnOk) {
      closeModal();
    }
  }
}
function thirdAction() {
  props.thirdFunction();
  emit('onThird');
  if (props.autoCloseOnThird) {
    closeModal();
  }
}
function cancelAction() {
  props.cancelFunction();
  emit('onCancel');
  if (props.autoCloseOnCancel) {
    closeModal(props.forceCloseOnCancel);
  }
}
function closeModal(force = false) {
  if (!props.noClose || force) {
    emit('onClose');
    open.value = false;
  }
}

defineExpose({
  openModal,
  closeModal,
  open,
});
</script>
